import React, { useState, useEffect } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from 'shared/api';
import { useUser } from 'shared/api/tanstack/queries';
import { ButtonCustom } from 'shared/ui/button-custom';
import styles from './draft-applications.module.scss';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface DraftApplication {
  id: number;
  documentId: string;
  bio?: string;
  email?: string;
  phone?: string;
  inn?: string;
  quantity?: string;
  car_type?: string;
  car_subtype?: string;
  car_brand?: string;
  vehicle?: string;
  pay_method?: string;
  condition?: string;
  // Поля для лизинговых заявок
  price?: string;
  initial_payment?: string;
  lease_period?: string;
  payment_type?: string;
  createdAt: string;
  updatedAt: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`draft-tabpanel-${index}`}
      aria-labelledby={`draft-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `draft-tab-${index}`,
    'aria-controls': `draft-tabpanel-${index}`,
  };
}

export const DraftApplicationsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectDrafts, setSelectDrafts] = useState<DraftApplication[]>([]);
  const [leasingDrafts, setLeasingDrafts] = useState<DraftApplication[]>([]);
  const [filteredSelectDrafts, setFilteredSelectDrafts] = useState<DraftApplication[]>([]);
  const [filteredLeasingDrafts, setFilteredLeasingDrafts] = useState<DraftApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { data: user } = useUser();

  // Состояния для поисковых фильтров
  const [searchFilters, setSearchFilters] = useState({
    bio: '',
    email: '',
    inn: '',
    phone: '',
  });

  // Состояния для множественного выбора
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedIds(new Set()); // Очищаем выбор при смене таба
  };

  // Функция фильтрации данных
  const filterDrafts = (drafts: DraftApplication[], filters: typeof searchFilters) => {
    return drafts.filter((draft) => {
      const bioMatch = !filters.bio || draft.bio?.toLowerCase().includes(filters.bio.toLowerCase());
      const emailMatch =
        !filters.email || draft.email?.toLowerCase().includes(filters.email.toLowerCase());
      const innMatch = !filters.inn || draft.inn?.toLowerCase().includes(filters.inn.toLowerCase());
      const phoneMatch =
        !filters.phone || draft.phone?.toLowerCase().includes(filters.phone.toLowerCase());

      return bioMatch && emailMatch && innMatch && phoneMatch;
    });
  };

  // Обработчик изменения поисковых фильтров
  const handleFilterChange = (field: keyof typeof searchFilters, value: string) => {
    const newFilters = { ...searchFilters, [field]: value };
    setSearchFilters(newFilters);

    // Применяем фильтры к текущему списку
    const filteredSelect = filterDrafts(selectDrafts, newFilters);
    const filteredLeasing = filterDrafts(leasingDrafts, newFilters);
    setFilteredSelectDrafts(filteredSelect);
    setFilteredLeasingDrafts(filteredLeasing);
  };

  // Функции для работы с выбором
  const getCurrentDrafts = () => (tabValue === 0 ? filteredSelectDrafts : filteredLeasingDrafts);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(getCurrentDrafts().map((draft) => draft.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const currentDrafts = getCurrentDrafts();
  const isAllSelected =
    currentDrafts.length > 0 && currentDrafts.every((draft) => selectedIds.has(draft.id));
  const isIndeterminate = selectedIds.size > 0 && !isAllSelected;

  const fetchDrafts = async () => {
    try {
      setLoading(true);

      // Получаем черновики select заявок
      const selectResponse = await apiClient.get('/select-applications/drafts');
      const selectData = selectResponse.data.data || [];
      setSelectDrafts(selectData);
      setFilteredSelectDrafts(selectData);

      // Получаем черновики leasing заявок
      const leasingResponse = await apiClient.get('/leasing-applications/drafts');
      const leasingData = leasingResponse.data.data || [];
      setLeasingDrafts(leasingData);
      setFilteredLeasingDrafts(leasingData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке черновиков:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchDrafts();
    }
  }, [user?.id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewDraft = (draft: DraftApplication, type: 'select' | 'leasing') => {
    navigate(`/dashboard/${user.isAdmin ? 'admin/' : ''}app/${type}/${draft.id || draft.documentId}`);
  };

  const handleDeleteDraft = async (draftId: number, type: 'select' | 'leasing') => {
    const prompt = await confirm(
      'Вы уверены, что хотите удалить выбранные черновики? Это действие нельзя отменить.',
    );
    if (!prompt) {
      return;
    }

    try {
      if (type === 'select') {
        await apiClient.delete(`/select-applications/${draftId}`);
        setSelectDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
        setFilteredSelectDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
      } else {
        await apiClient.delete(`/leasing-applications/${draftId}`);
        setLeasingDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
        setFilteredLeasingDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при удалении черновика:', error);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size > 0) {
      setShowDeleteDialog(true);
    }
  };

  const performBulkDelete = async () => {
    try {
      const currentType = tabValue === 0 ? 'select' : 'leasing';
      const deletePromises = Array.from(selectedIds).map((id) =>
        apiClient.delete(`/${currentType === 'select' ? 'select' : 'leasing'}-applications/${id}`),
      );

      const prompt = await confirm(
        'Вы уверены, что хотите удалить выбранные черновики? Это действие нельзя отменить.',
      );
      if (!prompt) {
        return;
      }

      await Promise.all(deletePromises);

      // Обновляем список после удаления
      await fetchDrafts();
      setSelectedIds(new Set());
      setShowDeleteDialog(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при удалении черновиков:', error);
    }
  };

  const renderTable = (type: 'select' | 'leasing') => {
    const drafts = type === 'select' ? filteredSelectDrafts : filteredLeasingDrafts;
    const originalDrafts = type === 'select' ? selectDrafts : leasingDrafts;

    // Показываем полное пустое состояние только если нет черновиков вообще
    if (originalDrafts.length === 0) {
      return renderEmptyState(type);
    }

    return (
      <Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedIds.size > 0 && (
              <>
                <Typography variant='body2' color='text.secondary'>
                  Выбрано: {selectedIds.size}
                </Typography>
                <ButtonCustom onClick={handleBulkDelete} className={styles.redBtn}>
                  Удалить выбранные
                </ButtonCustom>
              </>
            )}
          </Box>
        </Box>

        <TableContainer component={Box} className={styles.tableContainer}>
          <Table className={styles.table} sx={{ minWidth: 1070 }} aria-label='Список черновиков'>
            <TableHead className={styles.tableHead}>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                >
                  {/* Пустая ячейка для колонки с чекбоксами */}
                </TableCell>
                <TableCell className={styles.tableHeadCell}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='ФИО'
                    value={searchFilters.bio}
                    onChange={(e) => handleFilterChange('bio', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='email'
                    value={searchFilters.email}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='ИНН'
                    value={searchFilters.inn}
                    onChange={(e) => handleFilterChange('inn', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='телефону'
                    value={searchFilters.phone}
                    onChange={(e) => handleFilterChange('phone', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Обновлено
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Действия
                </TableCell>
              </TableRow>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    color='primary'
                  />
                </TableCell>
                <TableCell className={styles.tableHeadCell}>ФИО</TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Email
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  ИНН
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Телефон
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Дата
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  -
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableBody}>
              {drafts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align='center' style={{ padding: '40px 20px' }}>
                    <Typography variant='h6' color='text.secondary'>
                      По вашему запросу ничего не найдено
                    </Typography>
                    <Typography variant='body2' color='text.secondary' style={{ marginTop: '8px' }}>
                      Попробуйте изменить критерии поиска
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                drafts.map((draft) => {
                  return (
                    <TableRow
                      key={draft.id}
                      className={styles.tableBodyRow}
                      sx={{
                        '& td, & th': { border: 0 },
                        marginTop: '10px',
                      }}
                    >
                      <TableCell
                        className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedIds.has(draft.id)}
                          onChange={(e) => handleSelectOne(draft.id, e.target.checked)}
                          color='primary'
                        />
                      </TableCell>
                      <TableCell className={styles.tableBodyCell} component='th' scope='row'>
                        {draft.bio || '-'}
                      </TableCell>
                      <TableCell
                        className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                        align='left'
                      >
                        {draft.email || '-'}
                      </TableCell>
                      <TableCell
                        className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                        align='left'
                      >
                        {draft.inn || '-'}
                      </TableCell>
                      <TableCell
                        className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                        align='left'
                      >
                        {draft.phone || '-'}
                      </TableCell>
                      <TableCell
                        className={styles.tableBodyCell}
                        align='center'
                        style={{ cursor: 'pointer' }}
                      >
                        <Typography variant='caption'>{formatDate(draft.updatedAt)}</Typography>
                      </TableCell>
                      <TableCell
                        className={styles.tableBodyCell}
                        align='center'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <Button
                            variant='outlined'
                            color='info'
                            onClick={() => handleViewDraft(draft, type)}
                            size='small'
                          >
                            Просмотр
                          </Button>
                          <Button
                            variant='outlined'
                            color='error'
                            onClick={() => handleDeleteDraft(draft.id, type)}
                            size='small'
                          >
                            Удалить
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderEmptyState = (type: 'select' | 'leasing') => (
    <Box className={styles.emptyState}>
      <Typography variant='h6' color='text.secondary' align='center'>
        Нет сохраненных черновиков
      </Typography>
      <Typography variant='body2' color='text.secondary' align='center'>
        {type === 'select'
          ? 'Черновики заявок на подбор будут отображаться здесь'
          : 'Черновики заявок на лизинг будут отображаться здесь'}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box className={styles.container}>
        <Typography variant='h4' className={styles.pageTitle}>
          Черновики заявок
        </Typography>
        <Box className={styles.loading}>
          <Typography>Загрузка...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Typography variant='h4' className={styles.pageTitle}>
        Черновики заявок
      </Typography>

      <Box className={styles.tabsContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label='draft applications tabs'
          className={styles.tabs}
        >
          <Tab label={`Подбор (${selectDrafts.length})`} {...a11yProps(0)} />
          <Tab label={`Лизинг (${leasingDrafts.length})`} {...a11yProps(1)} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderTable('select')}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderTable('leasing')}
      </TabPanel>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby='bulk-delete-dialog-title'
        aria-describedby='bulk-delete-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '400px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='bulk-delete-dialog-title'>
          <span>Подтверждение удаления</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить {selectedIds.size}{' '}
            {selectedIds.size === 1 ? 'запись' : selectedIds.size < 5 ? 'записи' : 'записей'}? Это
            действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom
            className={styles.modalButtonCancel}
            onClick={() => setShowDeleteDialog(false)}
          >
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.redBtn} onClick={performBulkDelete}>
            Удалить
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
